import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Heart, Bookmark, Share2, MoreHorizontal, Bot, Send, Paperclip, Mic, CornerDownLeft } from "lucide-react";
import { Button } from "./ui/button";

interface Author {
  username: string;
  avatar_url: string;
  id: string;
}

interface Pin {
  id: string;
  image_url: string;
  description?: string;
  category?: string;
  user_id?: string;
  author?: Author;
  created_at?: string;
}

export const InkPinsGrid = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [likedPins, setLikedPins] = useState<Set<string>>(new Set());
  const [savedPins, setSavedPins] = useState<Set<string>>(new Set());
  const pageSize = 20;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["pins", activeTag],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        let query = supabase
          .from("posts")
          .select(`
            id,
            image_url,
            description,
            category,
            created_at,
            user_id,
            profiles:user_id (username, avatar_url, id)
          `)
          .order("created_at", { ascending: false })
          .range(pageParam * pageSize, (pageParam + 1) * pageSize - 1);
        
        if (activeTag) {
          query = query.eq("category", activeTag);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error("Error fetching pins:", error);
          throw error;
        }
        
        return data?.map(pin => ({
          id: pin.id,
          image_url: pin.image_url,
          description: pin.description,
          category: pin.category,
          user_id: pin.user_id,
          created_at: pin.created_at,
          author: pin.profiles ? {
            username: pin.profiles.username || "Anonymous",
            avatar_url: pin.profiles.avatar_url || "/placeholder.svg",
            id: pin.profiles.id
          } : undefined
        })) || [];
      } catch (error) {
        console.error("Error in queryFn:", error);
        toast.error("Error loading pins");
        return [];
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === pageSize ? allPages.length : undefined;
    },
    initialPageParam: 0
  });

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const handleLike = (e: React.MouseEvent, pinId: string) => {
    e.stopPropagation();
    setLikedPins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pinId)) {
        newSet.delete(pinId);
      } else {
        newSet.add(pinId);
      }
      return newSet;
    });
    toast.success("Status de curtida atualizado");
  };

  const handleSave = (e: React.MouseEvent, pinId: string) => {
    e.stopPropagation();
    setSavedPins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pinId)) {
        newSet.delete(pinId);
      } else {
        newSet.add(pinId);
      }
      return newSet;
    });
    toast.success("Status de salvamento atualizado");
  };

  const handleShare = (e: React.MouseEvent, pinId: string) => {
    e.stopPropagation();
    toast.success("Link copiado para a área de transferência");
  };

  const filterByTag = (tag: string | null) => {
    setActiveTag(tag);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-4">
        <motion.div 
          className="flex gap-2 mb-6 overflow-x-auto no-scrollbar px-2 py-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {["new", "manga", "digital-art"].map((tag, index) => (
            <motion.div 
              key={tag}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="px-4 py-1 bg-neutral-100 dark:bg-neutral-800 rounded-full text-sm whitespace-nowrap animate-pulse"
              style={{ width: '80px', height: '30px' }}
            />
          ))}
        </motion.div>
        <motion.div 
          className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <motion.div 
              key={i} 
              variants={itemVariants}
              className="rounded-lg animate-pulse bg-neutral-100 dark:bg-neutral-800 break-inside-avoid mb-4" 
              style={{ height: `${Math.floor(Math.random() * 150) + 200}px` }} 
            />
          ))}
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="container mx-auto py-4 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-red-500">Erro ao carregar pins. Por favor, tente novamente mais tarde.</p>
      </motion.div>
    );
  }

  const categories = Array.from(new Set(data?.pages.flatMap(page => page.map(pin => pin.category)).filter(Boolean) || []));
  const pins = data?.pages.flatMap(page => page) || [];

  return (
    <div className="container mx-auto py-0 px-0">
      <motion.div 
        className="flex gap-2 mb-6 overflow-x-auto no-scrollbar px-2 py-1"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.button 
          className={`px-4 py-1 ${!activeTag ? 'bg-primary text-primary-foreground' : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'} rounded-full text-sm whitespace-nowrap transition-colors`}
          onClick={() => filterByTag(null)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Todos os Pins
        </motion.button>
        {categories.map((category, index) => category && (
          <motion.button 
            key={category}
            className={`px-4 py-1 ${activeTag === category ? 'bg-primary text-primary-foreground' : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'} rounded-full text-sm whitespace-nowrap transition-colors`}
            onClick={() => filterByTag(category)}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            #{category}
          </motion.button>
        ))}
      </motion.div>
      
      <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-4 px-4 space-y-4">
        {pins?.map((pin) => (
          <motion.div 
            key={pin.id} 
            className="mb-4 group relative overflow-hidden rounded-lg cursor-pointer break-inside-avoid"
            onClick={() => navigate(`/pin/${pin.id}`)}
            onMouseEnter={() => setHoveredId(pin.id)}
            onMouseLeave={() => setHoveredId(null)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Card className="overflow-hidden">
              <img 
                src={pin.image_url} 
                alt={pin.description?.substring(0, 30) || "Pin"} 
                className="w-full h-auto transition-all duration-300 group-hover:brightness-90" 
                style={{ objectFit: "cover" }}
              />
              
              <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <motion.button
                  className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => handleLike(e, pin.id)}
                >
                  <Heart 
                    className={`h-5 w-5 ${likedPins.has(pin.id) ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} 
                  />
                </motion.button>
                
                <motion.button
                  className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => handleSave(e, pin.id)}
                >
                  <Bookmark 
                    className={`h-5 w-5 ${savedPins.has(pin.id) ? 'text-blue-500 fill-blue-500' : 'text-gray-600'}`} 
                  />
                </motion.button>
                
                <motion.button
                  className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => handleShare(e, pin.id)}
                >
                  <Share2 className="h-5 w-5 text-gray-600" />
                </motion.button>
              </div>
              
              <AnimatePresence>
                {hoveredId === pin.id && pin.author && (
                  <motion.div 
                    className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm p-3 flex items-center gap-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={pin.author.avatar_url} 
                      alt={pin.author.username} 
                      className="h-8 w-8 rounded-full border-2 border-white/50 object-cover" 
                    />
                    <div>
                      <p className="text-white text-sm font-medium">{pin.author.username}</p>
                      {pin.category && (
                        <span className="text-white/80 text-xs">
                          #{pin.category}
                        </span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        ))}
      </div>

      {pins && pins.length >= pageSize && (
        <div className="flex justify-center mt-8 mb-8">
          <Button
            onClick={handleLoadMore}
            variant="outline"
            size="lg"
            className="min-w-[200px]"
            disabled={!hasNextPage || isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Carregando...
              </span>
            ) : hasNextPage ? (
              "Ver mais"
            ) : (
              "Sem mais pins"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
